import {
	ResponsiveIndicator,
	ScrollableMarker,
} from '@sohanemon/utils/components';
import { stackProviders } from '@sohanscript/lib/utils/stack-providers';
import { ThemeFavicon, ThemeSync } from '@sohanscript/theme/ui';
import { LenisScroll } from '@sohanscript/ui/animated/lenis-scroll';
import { Toaster } from '@sohanscript/ui/sonner';
import { SurfaceDock, SurfaceDockProvider } from '@sohanscript/ui/surface-dock';
import { TooltipProvider } from '@sohanscript/ui/tooltip';
import { ClientOnly } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import { MotionConfig } from 'motion/react';
import { lazy, type ReactNode, Suspense, useSyncExternalStore } from 'react';
import { DevMenu } from '@/components/features/dev-menu';
import { getDevSettingSync } from '@/components/features/dev-menu/config';
import { useDraft } from '../hooks/use-draft';

const PostHogProvider = lazy(() =>
	import('#/lib/context/post-hog-provider.tsx').then((module) => ({
		default: module.PostHogProvider,
	})),
);

function subscribeToResponsiveIndicator() {
	return () => {};
}

function getResponsiveIndicatorSnapshot() {
	return getDevSettingSync('responsiveIndicator');
}

function getResponsiveIndicatorServerSnapshot() {
	return false;
}

export const Providers = stackProviders([
	function Motion({ children }) {
		return <MotionConfig reducedMotion="user">{children}</MotionConfig>;
	},
	TooltipProvider,
	function SurfaceDockShell({ children }: { children: ReactNode }) {
		return (
			<SurfaceDockProvider>
				{children}
				<SurfaceDock />
			</SurfaceDockProvider>
		);
	},
	function PostHogWrapper({ children }) {
		const { active } = useDraft();
		return (
			<>
				{children}
				{active ? null : (
					<Hydrate when={idle()}>
						<Suspense fallback={null}>
							<PostHogProvider />
						</Suspense>
					</Hydrate>
				)}
			</>
		);
	},
	function ClientStuff({ children }) {
		const { active } = useDraft();
		const showResponsiveIndicator = useSyncExternalStore(
			subscribeToResponsiveIndicator,
			getResponsiveIndicatorSnapshot,
			getResponsiveIndicatorServerSnapshot,
		);

		return (
			<>
				{children}
				<ClientOnly>
					<ThemeSync />
					<ThemeFavicon />
					<Toaster />
					<LenisScroll />
					<ScrollableMarker />
					{active ? null : (
						<>
							<DevMenu />
							{showResponsiveIndicator && <ResponsiveIndicator />}
						</>
					)}
				</ClientOnly>
			</>
		);
	},
]);
